import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, AlertTriangle, CheckCircle, RefreshCw, 
  Calendar, User, Edit2, Save, X 
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MissedCheckouts = () => {
  const [missedCheckouts, setMissedCheckouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [editDialog, setEditDialog] = useState({ open: false, record: null });
  const [checkoutTime, setCheckoutTime] = useState('18:00');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchMissedCheckouts();
  }, []);

  const fetchMissedCheckouts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/staff-attendance/missed-checkouts?start_date=${startDate}&end_date=${endDate}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setMissedCheckouts(data.missed_checkouts || []);
      } else {
        throw new Error('Failed to fetch data');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load missed checkouts');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCheckout = (record) => {
    setEditDialog({ open: true, record });
    setCheckoutTime(record.check_out_time ? record.check_out_time.split('T')[1]?.substring(0, 5) : '18:00');
    setRemarks('');
  };

  const handleSaveCheckout = async () => {
    if (!editDialog.record) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/staff-attendance/edit-checkout/${editDialog.record.employee_id}/${editDialog.record.date}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            check_out_time: checkoutTime,
            remarks: remarks || 'Manually set by admin'
          })
        }
      );

      if (response.ok) {
        toast.success('Checkout time updated successfully');
        setEditDialog({ open: false, record: null });
        fetchMissedCheckouts();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to update checkout time');
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (isoTime) => {
    if (!isoTime) return '-';
    try {
      const date = new Date(isoTime);
      return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch {
      return isoTime;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="missed-checkouts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <AlertTriangle className="text-amber-500" size={28} />
            Missed Checkouts
          </h1>
          <p className="text-gray-600 mt-1">Review and fix staff who forgot to checkout</p>
        </div>
        <Button onClick={fetchMissedCheckouts} variant="outline" data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-44">
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-input"
              />
            </div>
            <div className="w-44">
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-input"
              />
            </div>
            <Button onClick={fetchMissedCheckouts} className="bg-blue-600 hover:bg-blue-700" data-testid="filter-btn">
              <Calendar className="h-4 w-4 mr-2" /> Filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{missedCheckouts.length}</p>
                <p className="text-sm text-gray-600">Missed Checkouts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">
                  {missedCheckouts.filter(r => r.auto_checked_out).length}
                </p>
                <p className="text-sm text-gray-600">Auto Checked Out</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {missedCheckouts.filter(r => r.manually_edited).length}
                </p>
                <p className="text-sm text-gray-600">Manually Fixed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Staff with Missed Checkouts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : missedCheckouts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p className="text-lg font-medium text-green-600">All Clear!</p>
              <p className="text-sm">No missed checkouts in selected date range</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="missed-checkouts-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Employee</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Check In</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Check Out</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Status</th>
                    <th className="text-center p-3 text-sm font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {missedCheckouts.map((record, index) => (
                    <tr key={`${record.staff_id}-${record.date}-${index}`} className="border-b hover:bg-amber-50">
                      <td className="p-3">
                        <p className="font-medium">{formatDate(record.date)}</p>
                      </td>
                      <td className="p-3">
                        <p className="font-medium">{record.staff_name}</p>
                        <p className="text-sm text-gray-500">{record.employee_id}</p>
                      </td>
                      <td className="p-3 text-green-600 font-medium">
                        {formatTime(record.check_in_time)}
                      </td>
                      <td className="p-3">
                        {record.check_out_time ? (
                          <span className="text-blue-600 font-medium">{formatTime(record.check_out_time)}</span>
                        ) : (
                          <span className="text-red-500">Not Set</span>
                        )}
                      </td>
                      <td className="p-3">
                        {record.manually_edited ? (
                          <Badge className="bg-green-100 text-green-700">Manually Fixed</Badge>
                        ) : record.auto_checked_out ? (
                          <Badge className="bg-blue-100 text-blue-700">Auto Checkout</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditCheckout(record)}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          data-testid={`edit-btn-${record.employee_id}`}
                        >
                          <Edit2 className="h-4 w-4 mr-1" />
                          Set Time
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, record: open ? editDialog.record : null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              Set Checkout Time
            </DialogTitle>
          </DialogHeader>
          
          {editDialog.record && (
            <div className="space-y-4 mt-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{editDialog.record.staff_name}</p>
                <p className="text-sm text-gray-500">{editDialog.record.employee_id} • {formatDate(editDialog.record.date)}</p>
                <p className="text-sm text-green-600 mt-1">
                  Check In: {formatTime(editDialog.record.check_in_time)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Checkout Time *
                </label>
                <Input
                  type="time"
                  value={checkoutTime}
                  onChange={(e) => setCheckoutTime(e.target.value)}
                  data-testid="checkout-time-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks (Optional)
                </label>
                <Input
                  type="text"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="e.g., Forgot to checkout"
                  data-testid="remarks-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setEditDialog({ open: false, record: null })}
                  disabled={saving}
                >
                  <X className="h-4 w-4 mr-1" /> Cancel
                </Button>
                <Button
                  onClick={handleSaveCheckout}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="save-checkout-btn"
                >
                  <Save className="h-4 w-4 mr-1" />
                  {saving ? 'Saving...' : 'Save Checkout'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MissedCheckouts;
